import { useContext, useEffect, useRef } from 'react'
import { SyncContext } from '../contexts/SyncProvider'
import { SYNC_IN_PROGRESS } from '../lib/sync/types'
import { getPlatformAPI } from '../lib/platform'

const RETRY_MS = 1000
const MAX_TRIES = 30

const NETWORK_PATTERNS = ['offline', 'fetch', 'network', 'timeout', 'econnrefused', 'err_name', 'load failed']

/**
 * Wie ein Lauf ausging, gemessen an der Frage „ist der Stand der Cloud jetzt da?“ (Diff-Audit
 * 11.10.2026): `success` ist schon falsch, wenn eine einzelne Zeile scheitert — geholt hat der Lauf
 * trotzdem. Nicht geholt ist nur, was am Netz, an der Anmeldung oder an einer Ausnahme hing.
 */
export function syncOutcome(errors: readonly string[]): { ok: true; synced: true } | { ok: false; reason: 'offline' | 'failed' } {
  if (errors.some(error => NETWORK_PATTERNS.some(pattern => error.toLowerCase().includes(pattern)))) return { ok: false, reason: 'offline' }
  if (errors.some(error => error.startsWith('Sync failed') || error === 'Not authenticated' || error.startsWith('No user ID'))) {
    return { ok: false, reason: 'failed' }
  }
  return { ok: true, synced: true }
}

/**
 * **Sync auf Bitte des Einstellungsfensters** (#158, Entscheidung 20). Vor dem Wiederherstellen eines
 * Backups muss der Stand der Cloud auf dem Gerät sein; das Einstellungsfenster hat aber keinen eigenen
 * Sync, und zwei Läufe nebeneinander schickten dieselben Einträge doppelt hoch. Es fragt darum über
 * den Hauptprozess hier an (`backup:sync-request`), und dieses Fenster antwortet (`backup:sync-done`).
 *
 * Läuft schon ein Sync, wird kurz gewartet und neu angefragt — die Antwort soll den Stand NACH einem
 * vollständigen Lauf melden.
 */
export function useBackupSyncBridge() {
  const sync = useContext(SyncContext)
  const latest = useRef(sync)
  latest.current = sync

  useEffect(() => {
    if (getPlatformAPI().platform !== 'electron' || !window.api?.receive) return
    const answer = (id: string, result: unknown) => void window.api.invoke('backup:sync-done', id, result)
    const cleanup = window.api.receive('backup:sync-request', async (id: string) => {
      for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
        const context = latest.current
        if (!context?.isSyncEnabled) return answer(id, { ok: true, synced: false })
        if (!context.isOnline) return answer(id, { ok: false, reason: 'offline' })
        const result = await context.sync()
        if (result.success || !result.errors.every(error => error === SYNC_IN_PROGRESS)) return answer(id, syncOutcome(result.errors))
        await new Promise(resolve => setTimeout(resolve, RETRY_MS))
      }
      answer(id, { ok: false, reason: 'failed' })
    })
    void window.api.invoke('backup:sync-bridge-ready')
    return () => {
      cleanup?.()
    }
  }, [])
}
